import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { createPaymentIntent } from '../services/paymentService';
import { formatAmountForDisplay, formatAmountForStripe } from '../services/stripeService';
import { Box, Button, FormControl, FormLabel, Input, Textarea, VStack, Text, useToast, Heading, Checkbox } from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';

const cardStyle = {
  style: {
    base: {
      color: '#1e293b',
      fontFamily: '"Poppins", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

/**
 * Payment form component for making payments to students
 * @param {Object} props
 * @param {string} props.companyId - Company ID
 * @param {string} props.studentId - Student ID
 * @param {string} props.projectId - Project ID
 * @param {string} props.prefilledAmount - Pre-filled amount value
 * @param {string} props.prefilledDescription - Pre-filled description value
 * @param {Function} props.onSuccess - Callback function on successful payment
 * @param {Function} props.onCancel - Callback function on cancel
 */
const PaymentForm = ({ companyId, studentId, projectId, prefilledAmount, prefilledDescription, onSuccess, onCancel }) => {
  const [amount, setAmount] = useState(prefilledAmount || '');
  const [description, setDescription] = useState(prefilledDescription || '');
  const [isLoading, setIsLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [saveCard, setSaveCard] = useState(true);
  
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }
    
    // Validate input
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      setPaymentError('Please enter a valid amount');
      return;
    }
    
    setIsLoading(true);
    setPaymentError(null);
    
    try {
      // Create payment intent via our backend
      const amountInCents = formatAmountForStripe(parseFloat(amount));
      const paymentData = {
        companyId,
        studentId,
        projectId,
        amount: amountInCents,
        currency: 'usd',
        description,
        setupFutureUsage: saveCard ? 'off_session' : undefined
      };
      
      const paymentIntent = await createPaymentIntent(paymentData);
      
      // Confirm card payment
      const cardElement = elements.getElement(CardElement);
      const { error, paymentIntent: confirmedPaymentIntent } = await stripe.confirmCardPayment(
        paymentIntent.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              // You could collect billing details here if needed
            },
          },
          setup_future_usage: saveCard ? 'off_session' : undefined
        }
      );
      
      if (error) {
        setPaymentError(error.message);
      } else if (confirmedPaymentIntent.status === 'succeeded') {
        setPaymentSuccess(true);
        toast({
          title: 'Payment successful!',
          description: `You've successfully paid ${formatAmountForDisplay(amountInCents)}. Email receipt sent!`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        if (onSuccess) {
          onSuccess(confirmedPaymentIntent);
        }
      }
    } catch (error) {
      setPaymentError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Box 
      maxW="100%" 
      mx="auto" 
      p={8} 
      borderRadius="16px"
      boxShadow="0 4px 12px rgba(0,0,0,0.05)"
      bg="white"
    >
      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          <Heading as="h3" size="lg" mb={3} color="#6c63ff">
            Payment Details
          </Heading>
          
          <FormControl isRequired>
            <FormLabel fontWeight="500" fontSize="lg">Amount (USD)</FormLabel>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              min="0.01"
              step="0.01"
              size="lg"
              borderRadius="8px"
              borderWidth="2px"
              borderColor="#eef1ff"
              _focus={{
                borderColor: "#6c63ff",
                boxShadow: "0 0 0 1px #6c63ff"
              }}
              _hover={{
                borderColor: "#6c63ff"
              }}
            />
          </FormControl>
          
          <FormControl>
            <FormLabel fontWeight="500" fontSize="lg">Description</FormLabel>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this payment for?"
              rows={3}
              size="lg"
              borderRadius="8px"
              borderWidth="2px"
              borderColor="#eef1ff"
              _focus={{
                borderColor: "#6c63ff",
                boxShadow: "0 0 0 1px #6c63ff"
              }}
              _hover={{
                borderColor: "#6c63ff"
              }}
            />
          </FormControl>
          
          <FormControl isRequired>
            <FormLabel fontWeight="500" fontSize="lg">Card Details</FormLabel>
            <Box 
              p={5} 
              borderWidth="2px" 
              borderRadius="8px" 
              borderColor="#eef1ff"
              bg="#f8fafc"
              _hover={{
                borderColor: "#6c63ff"
              }}
            >
              <CardElement options={cardStyle} />
            </Box>
            <Checkbox 
              isChecked={saveCard} 
              onChange={(e) => setSaveCard(e.target.checked)}
              mt={3}
              colorScheme="purple"
            >
              <Text fontSize="md" color="#666">
                Save this card for future payments
              </Text>
            </Checkbox>
          </FormControl>
          
          {paymentError && (
            <Box 
              p={4} 
              borderRadius="8px" 
              bg="rgba(239, 68, 68, 0.1)" 
              color="#ef4444"
            >
              <Text fontSize="md">{paymentError}</Text>
            </Box>
          )}
          
          {paymentSuccess && (
            <Box 
              p={4} 
              borderRadius="8px" 
              bg="rgba(34, 197, 94, 0.1)" 
              color="#22c55e"
            >
              <Text fontWeight="500" fontSize="md">Payment completed successfully!</Text>
            </Box>
          )}
          
          <Box display="flex" justifyContent="space-between" mt={4} gap={4}>
            <Button 
              onClick={onCancel} 
              isDisabled={isLoading}
              variant="ghost"
              color="#6c63ff"
              size="lg"
              px={8}
              leftIcon={<ArrowBackIcon />}
              _hover={{
                bg: "rgba(108, 99, 255, 0.1)"
              }}
            >
              Back
            </Button>
            
            <Box flex={1} />
            
            <Button 
              onClick={onCancel} 
              isDisabled={isLoading}
              variant="outline"
              borderWidth="2px"
              borderColor="#6c63ff"
              color="#6c63ff"
              borderRadius="50px"
              size="lg"
              px={8}
              _hover={{
                bg: "rgba(108, 99, 255, 0.1)",
                transform: "translateY(-2px)"
              }}
              transition="all 0.3s ease"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              isLoading={isLoading}
              isDisabled={!stripe || paymentSuccess}
              bgGradient="linear(to-r, #6c63ff, #3b82f6)"
              color="white"
              borderRadius="50px"
              size="lg"
              px={8}
              boxShadow="0 4px 15px rgba(108, 99, 255, 0.3)"
              _hover={{
                bgGradient: "linear(to-r, #6c63ff, #3b82f6)",
                transform: "translateY(-2px)",
                boxShadow: "0 6px 20px rgba(108, 99, 255, 0.4)"
              }}
              transition="all 0.3s ease"
            >
              {paymentSuccess ? 'Paid' : 'Pay Now'}
            </Button>
          </Box>
        </VStack>
      </form>
    </Box>
  );
};

export default PaymentForm; 